/* eslint-disable react/jsx-filename-extension */
import {
  AccessControl,
  DateTimeFormatter,
  Emoji,
  withErrorBoundary,
  getPriorityBadgeStatus
} from '@nile/components';
import { TicketDisplayIdContainer } from '@nile/mangifera/src/Components/Ticket';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  DisplayText,
  Card as DSCard,
  Link,
  TextStyle
} from '@superops/ui-design-system';
import React, { ReactNode } from 'react';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { MarkAsReadButton } from './Notifications';
import { getTimeAgo } from './time';
import AlertCardDetails from './AlertCardDetails';
import { KB_ARTICLE_MODULE } from '@nile/rmm/src/modules/knowledge-base/helper/constants';

const getNotificationReadColor = (isRead) => (isRead ? '#F5F5FE' : '#E2ECFF');

export const NOTIFICATION_TYPES = {
  TECH_VENDOR_REPLY: 'TECH_VENDOR_REPLY',
  TECH_MENTION: 'TECH_MENTION',
  TICKET_TECH_CREATION: 'TICKET_TECH_CREATION',
  TICKET_TECH_ASSIGNMENT: 'TICKET_TECH_ASSIGNMENT',
  TICKET_TECH_GROUP_ASSIGNMENT: 'TICKET_TECH_GROUP_ASSIGNMENT',
  TICKET_TECH_REQ_REPLY: 'TICKET_TECH_REQ_REPLY',
  TICKET_TECH_NOTE_ADDED: 'TICKET_TECH_NOTE_ADDED',
  TICKET_TECH_APPROVAL_STATUS: 'TICKET_TECH_APPROVAL_STATUS',
  TICKET_TECH_APPROVAL_ACTION_TAKEN: 'TICKET_TECH_APPROVAL_ACTION_TAKEN',
  TICKET_TECH_FR_SLA_ESCALATION: 'TICKET_TECH_FR_SLA_ESCALATION',
  TICKET_TECH_RES_SLA_ESCALATION: 'TICKET_TECH_RES_SLA_ESCALATION',
  CLIENT_TECH_QUOTE_APPROVAL: 'CLIENT_TECH_QUOTE_APPROVAL',
  CLIENT_TECH_QUOTE_REJECTION: 'CLIENT_TECH_QUOTE_REJECTION',
  CLIENT_TECH_REQ_REPLY: 'CLIENT_TECH_REQ_REPLY',
  CLIENT_TECH_NEW_SALES_EMAIL: 'CLIENT_TECH_NEW_SALES_EMAIL',
  TASK_TECH_ASSIGNED: 'TASK_TECH_ASSIGNED',
  TASK_TECH_DUE_TODAY: 'TASK_TECH_DUE_TODAY',
  TASK_TECH_OVERDUE: 'TASK_TECH_OVERDUE',
  ASSET_ALERT: 'ASSET_ALERT',
  TICKET_TECH_TECHNICIAN_APPROVAL: 'TICKET_TECH_TECHNICIAN_APPROVAL',
  KB_FEEDBACK_ADDED: 'FEEDBACK_ADDED'
};

// eslint-disable-next-line no-undef
export const NOTIFICATION_STATE: Record<
  'READ' | 'UNREAD',
  'READ' | 'UNREAD'
> = {
  READ: 'READ',
  UNREAD: 'UNREAD'
};

const TITLE = {
  [NOTIFICATION_TYPES.TECH_VENDOR_REPLY]: 'techVendorReply',
  [NOTIFICATION_TYPES.TECH_MENTION]: 'techMention',
  [NOTIFICATION_TYPES.TICKET_TECH_CREATION]: 'ticketTechCreation',
  [NOTIFICATION_TYPES.TICKET_TECH_ASSIGNMENT]: 'ticketTechAssignment',
  [NOTIFICATION_TYPES.TICKET_TECH_GROUP_ASSIGNMENT]:
    'ticketTechGroupAssignment',
  [NOTIFICATION_TYPES.TICKET_TECH_REQ_REPLY]: 'ticketReqReply',
  [NOTIFICATION_TYPES.TICKET_TECH_NOTE_ADDED]: 'ticketTechNoteAdded',
  [NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_STATUS]: 'ticketTechApprovalStatus',
  [NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_ACTION_TAKEN]:
    'ticketTechApprovalStatusUpdated',
  [NOTIFICATION_TYPES.TICKET_TECH_FR_SLA_ESCALATION]:
    'ticketTechFrSlaEscalation',
  [NOTIFICATION_TYPES.TICKET_TECH_RES_SLA_ESCALATION]:
    'ticketTechResSlaEscalation',
  [NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_APPROVAL]: 'clientTechQuoteApproval',
  [NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_REJECTION]: 'clientTechQuoteRejection',
  [NOTIFICATION_TYPES.CLIENT_TECH_REQ_REPLY]: 'clientTechReqReply',
  [NOTIFICATION_TYPES.CLIENT_TECH_NEW_SALES_EMAIL]: 'clientTechNewSalesEmail',
  [NOTIFICATION_TYPES.ASSET_ALERT]: 'alertNotify',
  [NOTIFICATION_TYPES.TICKET_TECH_TECHNICIAN_APPROVAL]: 'techRunbookApproval'
};

const EMOJI_MAP = {
  [NOTIFICATION_TYPES.TECH_VENDOR_REPLY]: '💬',
  [NOTIFICATION_TYPES.TECH_MENTION]: '💬',
  [NOTIFICATION_TYPES.TICKET_TECH_CREATION]: '🎟️',
  [NOTIFICATION_TYPES.TICKET_TECH_ASSIGNMENT]: '🎟️',
  [NOTIFICATION_TYPES.TICKET_TECH_GROUP_ASSIGNMENT]: '🎟️',
  [NOTIFICATION_TYPES.TICKET_TECH_REQ_REPLY]: '💬',
  [NOTIFICATION_TYPES.TICKET_TECH_NOTE_ADDED]: '📝',
  [NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_STATUS]: '✅',
  [NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_ACTION_TAKEN]: '✅',
  [NOTIFICATION_TYPES.TICKET_TECH_FR_SLA_ESCALATION]: '⏳',
  [NOTIFICATION_TYPES.TICKET_TECH_RES_SLA_ESCALATION]: '⏳',
  [NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_APPROVAL]: '✅',
  [NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_REJECTION]: '❌',
  [NOTIFICATION_TYPES.CLIENT_TECH_REQ_REPLY]: '💬',
  [NOTIFICATION_TYPES.CLIENT_TECH_NEW_SALES_EMAIL]: '💬',
  [NOTIFICATION_TYPES.TASK_TECH_ASSIGNED]: '💬',
  [NOTIFICATION_TYPES.TASK_TECH_DUE_TODAY]: '⏰',
  [NOTIFICATION_TYPES.TASK_TECH_OVERDUE]: '⏰',
  [NOTIFICATION_TYPES.ASSET_ALERT]: '⚠️',
  [NOTIFICATION_TYPES.TICKET_TECH_TECHNICIAN_APPROVAL]: '‼️'
};

const NO_VARIANT_MAP = {
  [NOTIFICATION_TYPES.TICKET_TECH_CREATION]:
    'notification.ticketTechCreation.noUserVariant',
  [NOTIFICATION_TYPES.TICKET_TECH_ASSIGNMENT]:
    'notification.ticketTechAssignment.noUserVariant',
  [NOTIFICATION_TYPES.TICKET_TECH_GROUP_ASSIGNMENT]:
    'notification.ticketTechGroupAssignment.noUserVariant',
  [NOTIFICATION_TYPES.TICKET_TECH_NOTE_ADDED]:
    'notification.ticketNoteAdded.noUserVariant'
};

const BadgeWrapper = styled.div`
  .Superops-Badge {
    background: #ececec;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Root = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

const Container = styled.div`
  background: ${(props) => getNotificationReadColor(props.isRead)};
  min-height: 95px;

  display: flex;
  padding: 5px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 10px;
  width: 100%;
  flex-basis: 90%;
  transition: all 200ms;
  opacity: ${(props) => (props.disabled ? '0.3' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};
  padding-top: 0;
  cursor: ${(props) => (props.disabled ? 'none' : 'pointer')};
  .emoji {
    font-size: 16px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  align-items: center;
  font-size: 12px !important;
  padding: 0 5px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
`;

const Time = styled.div`
  margin-left: auto;
  opacity: 0.6;
`;

const Content = styled.div`
  background: #fff;
  padding: 10px;
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContentSubject = styled(Center)`
  background: #f5f5fe;
  font-size: 12px;
  font-weight: ${({ isBold = true }) => (isBold ? '600' : '400')};
  height: 30px;
  border-radius: 10px;
  justify-content: flex-start;
  padding: 0px 10px;
  display: flex;
  ${(props) => {
    if (props.gap) {
      return `gap: ${props.gap}px`;
    }
  }}
`;

const ContentFooter = styled.div`
  font-size: 12px;
  font-weight: 400;
  align-self: start;
`;

const ContentDetails = styled(Center)`
  gap: 10px;
  flex-direction: column;
`;

const ContentPrimary = styled(Center)`
  gap: 10px;
  align-self: start;
  align-items: flex-start;
`;

const WorkItemSubject = styled.div`
  align-self: start;
  font-weight: 500;
`;

const EmojiWrap = styled.div`
  min-width: 20px;
  min-height: 20px;
`;

type ModuleType = 'INVOICE' | 'TICKET' | 'PROJECT' | 'QUOTE' | 'TASK';

function getContentEmoji(type: ModuleType) {
  switch (type) {
    case 'TICKET':
      return <SVGIcon name="TicketFilled" />;
    case 'PROJECT':
      return <SVGIcon name="ProjectFilled" />;
    case 'INVOICE':
      return <SVGIcon name="InvoiceModule" />;
    case 'QUOTE':
      return <SVGIcon name="QuoteModule" />;
    default:
      throw Error('Incorrect type for emoji');
  }
}

function getTitleEmoji(notificationType) {
  const emoji = EMOJI_MAP[notificationType];

  if (
    notificationType === NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_STATUS ||
    notificationType === NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_ACTION_TAKEN
  ) {
    return null;
  }
  if (notificationType === NOTIFICATION_TYPES.KB_FEEDBACK_ADDED) {
    return <SVGIcon name="ReleaseNotes" />;
  }

  return (
    <div className="mr12 emoji">
      <Emoji symbol={emoji} label={notificationType} />
    </div>
  );
}

interface BaseCardProps {
  time: string;
  content: ReactNode;
  onMarkAsRead: () => void;
  eventType: string;
  isRead: boolean;
  workItem: any;
  tabRef: any;
  user: any;
  emailAddress: any;
  clientConversation: any;
  vendorConversation: any;
  disabled: boolean;
  onClick: () => void;
  task: any;
  alert: any;
  approval: {
    additionalMessage: string;
  };
}

function BaseCard({
  time,
  content,
  onMarkAsRead,
  eventType,
  isRead,
  workItem,
  tabRef,
  user,
  clientConversation,
  vendorConversation,
  disabled,
  onClick,
  emailAddress,
  task
}: BaseCardProps) {
  const tabContext = tabRef.current;
  const redirectToEntity = () => {
    // eslint-disable-next-line no-unused-expressions
    onClick?.();
    if (workItem?.module) {
      switch (workItem?.module) {
        case 'TICKET':
          if (!workItem?.client) return;
          tabContext.openTabCb({
            module: 'TICKET',
            data: {
              subject: workItem?.subject,
              displayId: workItem?.displayId,
              ticketId: workItem?.workId
            },
            state: {
              detailsTabIndex: vendorConversation ? 4 : 0
            },
            ticketId: workItem?.workId
          });
          break;
        case 'INVOICE':
          tabContext.openTabCb({
            module: 'INVOICE',
            data: {
              displayId: workItem?.displayId,
              invoiceId: workItem?.workId
            }
          });
          break;
        case 'QUOTE':
          tabContext.openTabCb({
            module: 'QUOTE',
            data: {
              displayId: workItem?.displayId,
              quoteId: workItem?.workId
            }
          });
          break;
        case 'PROJECT':
          tabContext.openTabCb({
            module: 'PROJECT_DETAIL',
            data: {
              title: workItem?.subject,
              projectId: workItem?.workId
            }
          });
          break;
        default:
      }
    } else if (clientConversation) {
      tabContext.openTabCb({
        module: 'CLIENT',
        data: {
          id: clientConversation?.client?.accountId,
          value: clientConversation?.client?.name
        },
        state: {
          detailsTabIndex: 1
        },
        url: `/client/${clientConversation?.client?.accountId}/detail/mailBoxViews/5004/All%20Conversations`,
        queryParams: `?parentConversationId=${clientConversation?.parentConversationId}&conversationId=${clientConversation?.conversationId}`
      });
    }
  };

  const renderTitle = () => {
    switch (eventType) {
      case NOTIFICATION_TYPES.TICKET_TECH_CREATION:
      case NOTIFICATION_TYPES.TICKET_TECH_ASSIGNMENT:
      case NOTIFICATION_TYPES.TICKET_TECH_GROUP_ASSIGNMENT:
      case NOTIFICATION_TYPES.TICKET_TECH_NOTE_ADDED:
        if (!user?.name && !user?.email && !emailAddress) {
          return (
            <FormattedMessage
              id={NO_VARIANT_MAP[eventType]}
              values={{
                TechGroupName: (
                  <b className="mr-5 ml-5">
                    {workItem?.ticket?.techGroup?.name}
                  </b>
                )
              }}
            />
          );
        }
        break;
      case NOTIFICATION_TYPES.TASK_TECH_DUE_TODAY:
        return <FormattedMessage id="notification.taskTech.dueToday" />;
      case NOTIFICATION_TYPES.TASK_TECH_OVERDUE:
        return <FormattedMessage id="notification.taskTech.overdue" />;
      case NOTIFICATION_TYPES.TASK_TECH_ASSIGNED:
        return (
          <FormattedMessage
            id="notification.taskTech.assigned"
            values={{
              UserName: (
                <b className="mr-5">
                  {user?.name || user?.email || emailAddress}
                </b>
              )
            }}
          />
        );
      case NOTIFICATION_TYPES.KB_FEEDBACK_ADDED:
        return (
          <FormattedMessage id="app.notifications.kb.feedback.added.title" />
        );
      default:
    }

    return (
      <FormattedMessage
        id={`notification.${TITLE[eventType]}`}
        values={{
          UserName: (
            <b className="mr-5">
              {user?.name ||
                user?.email ||
                clientConversation?.user?.name ||
                clientConversation?.user?.email ||
                vendorConversation?.user?.name ||
                vendorConversation?.user?.email ||
                emailAddress}
            </b>
          ),
          TechGroupName: (
            <b className="mr-5 ml-5">{workItem?.ticket?.techGroup?.name}</b>
          )
        }}
      />
    );
  };

  return (
    <Root>
      <Container isRead={isRead} onClick={redirectToEntity} disabled={disabled}>
        <HeaderSection>
          <Title>
            {getTitleEmoji(eventType)}
            {renderTitle()}
          </Title>
          <Time>
            <Tooltip
              html={<DateTimeFormatter type="dateTime" date={time} />}
              position="bottom"
              delay={300}
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
              className="full-width"
            >
              {getTimeAgo(time)}
            </Tooltip>
          </Time>
        </HeaderSection>
        <Content>{content}</Content>
      </Container>
      <MarkAsReadButton
        onClick={onMarkAsRead}
        isVisible={!isRead}
        disabled={disabled}
      />
    </Root>
  );
}

function renderDisplayId(
  moduleType: ModuleType,
  displayId: string,
  ticket?: any
) {
  // eslint-disable-next-line no-param-reassign
  displayId = `#${displayId}`;

  switch (moduleType) {
    case 'PROJECT':
    case 'TASK':
      return (
        <Badge status={getPriorityBadgeStatus(ticket?.priority?.toLowerCase())}>
          {displayId}
        </Badge>
      );
    case 'INVOICE':
    case 'QUOTE':
      return (
        <BadgeWrapper>
          <Badge>{displayId}</Badge>
        </BadgeWrapper>
      );
    default:
  }

  if (moduleType === 'TICKET') {
    return <TicketDisplayIdContainer ticket={ticket} />;
  }

  throw Error('Check module type');
}

// eslint-disable-next-line no-undef
type CardProps = Omit<BaseCardProps, 'content'> & {
  vendorConversation: any;
  clientConversation: any;
  mention: any;
};

function Card(props: CardProps) {
  switch (props.eventType) {
    case NOTIFICATION_TYPES.TECH_VENDOR_REPLY: {
      const {
        isRead,
        workItem: { module, displayId, client, ticket },
        vendorConversation
      } = props;
      const conversation = vendorConversation;

      return (
        <BaseCard
          {...props}
          content={
            <>
              <ContentSubject>{conversation.subject}</ContentSubject>
              <ContentDetails>
                <ContentPrimary>
                  <EmojiWrap>{getContentEmoji(module)}</EmojiWrap>
                  {renderDisplayId(module, displayId, {
                    ...props?.workItem?.ticket,
                    displayId
                  })}
                  <WorkItemSubject>{props.workItem.subject}</WorkItemSubject>
                </ContentPrimary>
                <ContentFooter>{client?.name}</ContentFooter>
              </ContentDetails>
            </>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.TECH_MENTION: {
      const {
        workItem: { module, displayId, client }
      } = props;
      return (
        <BaseCard
          {...props}
          content={
            <ContentDetails>
              <ContentPrimary>
                {getContentEmoji(module)}
                {renderDisplayId(module, displayId, {
                  displayId
                  // priority,
                  // source,
                  // ticketType
                })}
                <WorkItemSubject>{props.workItem.subject}</WorkItemSubject>
              </ContentPrimary>
              <ContentFooter>{client?.name}</ContentFooter>
            </ContentDetails>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.TICKET_TECH_CREATION:
    case NOTIFICATION_TYPES.TICKET_TECH_ASSIGNMENT:
    case NOTIFICATION_TYPES.TICKET_TECH_GROUP_ASSIGNMENT:
    case NOTIFICATION_TYPES.TICKET_TECH_REQ_REPLY:
    case NOTIFICATION_TYPES.TICKET_TECH_NOTE_ADDED:
    case NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_STATUS:
    case NOTIFICATION_TYPES.TICKET_TECH_APPROVAL_ACTION_TAKEN:
    case NOTIFICATION_TYPES.TICKET_TECH_FR_SLA_ESCALATION:
    case NOTIFICATION_TYPES.TICKET_TECH_RES_SLA_ESCALATION:
    case NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_APPROVAL:
    case NOTIFICATION_TYPES.CLIENT_TECH_QUOTE_REJECTION: {
      const {
        workItem: { module, displayId, client }
      } = props;
      return (
        <BaseCard
          {...props}
          content={
            <ContentDetails>
              <ContentPrimary>
                {renderDisplayId(module, displayId, {
                  ...props?.workItem?.ticket,
                  displayId
                })}
                <WorkItemSubject>{props.workItem.subject}</WorkItemSubject>
              </ContentPrimary>
              <AccessControl
                productToCheck="MSP"
                render={<ContentFooter>{client?.name}</ContentFooter>}
              />
            </ContentDetails>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.TICKET_TECH_TECHNICIAN_APPROVAL: {
      const {
        workItem: { module, displayId, client }
      } = props;
      return (
        <BaseCard
          {...props}
          content={
            <ContentDetails>
              <ContentPrimary>
                {renderDisplayId(module, displayId, {
                  ...props?.workItem?.ticket,
                  displayId
                })}
                <WorkItemSubject>{props.workItem.subject}</WorkItemSubject>
              </ContentPrimary>
              {props.approval?.additionalMessage && (
                <div className="br8 approval-notification-bg full-width pt10 pr-20 pb10 pl-20">
                  <TextStyle>{props.approval.additionalMessage}</TextStyle>
                </div>
              )}
              <AccessControl
                productToCheck="MSP"
                render={<ContentFooter>{client?.name}</ContentFooter>}
              />
            </ContentDetails>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.CLIENT_TECH_NEW_SALES_EMAIL:
    case NOTIFICATION_TYPES.CLIENT_TECH_REQ_REPLY: {
      const { clientConversation } = props;
      return (
        <BaseCard
          {...props}
          content={
            <ContentDetails>
              <WorkItemSubject>{clientConversation.subject}</WorkItemSubject>
            </ContentDetails>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.ASSET_ALERT: {
      const { alert, tabRef: tabContext } = props;
      return (
        <BaseCard
          {...props}
          content={<AlertCardDetails alert={alert} tabContext={tabContext} />}
        />
      );
    }

    case NOTIFICATION_TYPES.TASK_TECH_DUE_TODAY:
    case NOTIFICATION_TYPES.TASK_TECH_OVERDUE:
    case NOTIFICATION_TYPES.TASK_TECH_ASSIGNED: {
      const { task, workItem, tabRef: tabContext } = props;

      return (
        <BaseCard
          {...props}
          content={
            <>
              <ContentSubject
                isBold={false}
                gap={10}
                onClick={(e) => {
                  /* Task Subject */
                  tabContext.current.openTabCb({
                    module: 'WORKSPACE',
                    url: `/tasks/4/task-list`,
                    queryParams: `?taskId=${task.taskId}&module=${workItem.module}`
                  });
                  e.stopPropagation();
                }}
              >
                {renderDisplayId('TASK', task.displayId, task)}
                <b>{task.title}</b>
              </ContentSubject>
              <ContentDetails>
                <ContentPrimary>
                  {getContentEmoji(workItem.module)}
                  {renderDisplayId(workItem.module, workItem.displayId, {
                    ...workItem.ticket,
                    displayId: workItem.displayId
                  })}
                  <WorkItemSubject>{workItem.subject}</WorkItemSubject>
                </ContentPrimary>
                <AccessControl
                  productToCheck="MSP"
                  render={
                    <ContentFooter>{workItem.client?.name}</ContentFooter>
                  }
                />
              </ContentDetails>
            </>
          }
        />
      );
    }

    case NOTIFICATION_TYPES.KB_FEEDBACK_ADDED: {
      const { kbArticleFeedback, tabRef: tabContext } = props;
      const { kbArticle, feedback } = kbArticleFeedback;
      return (
        <BaseCard
          {...props}
          content={
            <>
              <div
                onClick={(e) => {
                  tabContext.current.openTabCb({
                    module: KB_ARTICLE_MODULE,
                    url: `/kb/article/${kbArticle?.itemId}/read`,
                    data: { id: kbArticle?.itemId }
                  });
                  e.stopPropagation();
                }}
                className="cp"
              >
                <div className="flex g5 mb10">
                  <div className="fx actr">
                    <SVGIcon name="KnowledgeBaseModule" />
                  </div>
                  <div className="maxW400">
                    <TextStyle variation="strong">
                      <div className="ellipsis">{feedback}</div>
                    </TextStyle>
                  </div>
                </div>
                <div>
                  <DisplayText size="medium">
                    <FormattedMessage id="app.notifications.kbFeedback.servicePortal.label" />
                  </DisplayText>
                </div>
              </div>
            </>
          }
        />
      );
    }

    default:
      return null;
  }
}

export default withErrorBoundary(Card);
